import { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import './style.scss';
import RequiredAsterisk from '../RequiredAsterisk';
import { SanitizerService } from '../../../services/sanitizer.service';
import ValidationInfo from '../../../validation/validation-info';
import type IFile from './models/file.model';
import type IValidatable from '../validatable.interface';
import type { IServerValidationError } from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';
import { t } from '../../../services/texts.service';
import { TranslationService } from '../../../services/translation.service';
import { Icon } from '../Icon';
import { IconMotif } from '../Icon/icon-motif.enum';

const maxFileSize = 15728640;
const maxNrOffFiles = 6;
const serverValidationName = 'uploadedFiles'; // For backwards compatibility

export interface IFileUploadState {
  value: IFile[];
  validationInfo: ValidationInfo;
}

interface IFileUploadProps {
  id: string;
  isRequired?: boolean;
  infoText?: string;
  state: IFileUploadState;
  legendText?: string;
  onFilesAdded?: (files: IFile[]) => void;
  onFileRemoved?: (file: IFile) => void;
}

export default forwardRef<IValidatable, IFileUploadProps>(function FileUpload({ id, isRequired = false, infoText, state, legendText, onFilesAdded = undefined, onFileRemoved = undefined }, ref) {
  // === STATE ===
  const [showValidFileFormats, setShowValidFileFormats] = useState(false);

  // === REFERENCES ===
  const fileInputRef = useRef<HTMLInputElement>(null);

  // === HANDLERS ===
  function getFileKey(file: IFile): string {
    return `${file.actualFile.name}-${file.actualFile.size}-${file.actualFile.lastModified}`;
  }

  function getRemoveFileAriaLabel(file: IFile): string {
    return `${t('Ta bort fil', 'Delete file')}: ${file.name}`;
  }

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${t('Lägg till filer', 'Upload files')} - `);
      ValidationEngine.for(updatedValidationInfo)
        .requiredIf(isRequired, state.value.length > 0)
        .maxLength(state.value, maxNrOffFiles)
        .lessThan(
          state.value.reduce((acc, curr) => acc + curr.size, 0),
          maxFileSize + 1,
          'common.filesizeexceeded',
        )
        .validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${t('Lägg till filer', 'Upload files')} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
      );
    },
  }));

  function handleFileInputChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const result: IFile[] = [];
    const files = event.currentTarget.files;
    if (files) {
      for (let i = 0; i < files.length; i += 1) {
        const file = files.item(i);
        if (file) {
          result.push({
            name: file.name,
            size: file.size,
            displaySize: file.size === 0 ? '0 MB' : `${parseFloat((file.size / 1048576).toFixed(1))} MB`,
            actualFile: file,
          });
        }
      }
    }
    onFilesAdded?.(result);
  }

  function handleRemoveFileClick(index: number): void {
    onFileRemoved?.(state.value[index]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div id={id} className="file-upload">
      <div className="mb-3">
        <fieldset>
          <legend>
            {legendText ?? t('Bifoga fil', 'Attach file')} {isRequired && <RequiredAsterisk />}
          </legend>
          <p>
            {infoText ??
              t('Här kan du bifoga filer. Du kan bifoga max 6 st filer. De får inte vara större än 15 MB totalt.', 'Attach files here. Max 6 files. The files may not exceed 15 MB in total.')}
          </p>
          <div className="mb-3">
            <button
              type="button"
              className={`btn btn-tertiary btn-icon-right btn-icon-chevron btn-tertiary-icon-rotatable ${showValidFileFormats ? '' : 'collapsed'}`}
              aria-expanded={showValidFileFormats ? 'true' : 'false'}
              aria-controls={`${id}-valid-file-formats-list`}
              onClick={() => {
                setShowValidFileFormats(!showValidFileFormats);
              }}
            >
              {t('Visa lista över tillåtna format', 'Allowed formats')}
            </button>
          </div>
          <div
            id={`${id}-valid-file-formats-list`}
            hidden={!showValidFileFormats}
            dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('common.validfileformats')) }}
          ></div>
          {state.value.length > 0 && (
            <div className="mb-3">
              <h3 className="h4 mb-3">{t('Bifogade filer', 'Uploaded files')}</h3>
              <ul id={`${id}-uploaded-files-list`} className="list-unstyled mb-0">
                {state.value.map((file, index) => (
                  <li key={getFileKey(file)} className="file-upload__uploaded-files-item">
                    <div className="d-flex align-items-start justify-content-start gap-1 mt-3 text-start">
                      <Icon motif={IconMotif.FileLines} altText="" className="file-upload__uploaded-file-icon flex-shrink-0" />
                      <span className="text-break">
                        {file.name} ({file.displaySize})
                      </span>
                    </div>
                    <button type="button" className="btn btn-tertiary btn-icon-left btn-icon-trash" aria-label={getRemoveFileAriaLabel(file)} onClick={() => handleRemoveFileClick(index)}>
                      {t('Ta bort fil', 'Delete file')}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <button id={`${id}-file-upload-button`} type="button" className="btn btn-secondary file-upload__input-label mb-0" onClick={() => fileInputRef.current?.click()}>
            {t('Lägg till filer', 'Upload files')}
          </button>
          <input
            aria-labelledby={`${id}-file-upload-button`}
            tabIndex={-1}
            ref={fileInputRef}
            id={`${id}-file-upload-input`}
            className={`d-none ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
            type="file"
            multiple
            onChange={handleFileInputChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.gif,.png,.txt"
          />
        </fieldset>
        {state.validationInfo.errors.map((error, index) => (
          <div key={index} className="invalid-feedback">
            {error}
          </div>
        ))}
      </div>
    </div>
  );
});
